import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import {
  LayoutDashboard, Users, Clock, Wallet,
  Laptop, TrendingUp, Scale, LogOut, GraduationCap,
  Bug, BarChart3, Settings, CheckCircle2,
  Copy, X, ChevronLeft, ChevronRight, Target, Sparkles, AlertTriangle,
  type LucideIcon,
} from "lucide-react";

import { HR_ROLES, MGR_ROLES } from "@hrms/types";

import { api } from "../lib/api.js";
import { useAuth } from "../hooks/useAuth.js";
import { useMe } from "../context/MeContext.js";
import { useNotifications, type NotifItem } from "../context/NotificationsContext.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { Tooltip } from "./ui/tooltip.js";
import { FestiveBadge } from "./ui/festive-badge.js";
import { Avatar } from "./ui/avatar.js";
import { ProfileCardDialog } from "./ProfileCardDialog.js";
import { useActiveFestival } from "../hooks/useActiveFestival.js";

/** Outline icons at the size of the coloured tile they sit in. The whole menu
 *  uses one weight so the rail reads as a single set rather than mixed line widths. */
const ICON = { size: 16, strokeWidth: 1.9 } as const;

/** A row's colour: its icon tile and its sub-item dots (`[data-tone]` in tokens.css). */
type NavTone = "brand" | "info" | "cyan" | "warning" | "accent" | "success" | "danger" | "rose";

interface NavChild {
  to: string;
  label: string;
  /** Each child is gated on its own module, so a group shows only what the role can open. */
  moduleKey: string;
  /** Matches the path exactly instead of as a prefix (Reviews lives at the parent path). */
  end?: boolean;
  /** Shown only when this returns true (default: always). */
  when?: (viewer: { role: string; isOnActivePip: boolean }) => boolean;
}

interface NavItem {
  /** A route for a single link; for a group, just a stable key (it is never navigated to). */
  to: string;
  label: string;
  icon: LucideIcon;
  tone: NavTone;
  /** Single links only. A group is gated through its children's moduleKeys. */
  moduleKey?: string;
  children?: NavChild[];
}

const isHr = (v: { role: string }) => HR_ROLES.has(v.role);

// Related modules are grouped so the menu stays short; unrelated ones stay
// separate (Grievance is confidential, so it keeps its own link). Routes are
// unchanged. Issues is not here on purpose -- it lives in the footer.
const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tone: "brand", moduleKey: "dashboard" },
  {
    to: "group:people", label: "People", icon: Users, tone: "info",
    children: [
      { to: "/employees",   label: "Employees",     moduleKey: "employees" },
      { to: "/contractors", label: "Contractors",   moduleKey: "contractors" },
      { to: "/org-chart",   label: "Org Hierarchy", moduleKey: "org-chart" },
      { to: "/offboarding", label: "Offboarding",   moduleKey: "offboarding" },
      // HR and managers, plus an employee who is on a PIP (to follow their own).
      {
        to: "/performance/pip", label: "PIP", moduleKey: "performance",
        when: v => HR_ROLES.has(v.role) || MGR_ROLES.has(v.role) || v.isOnActivePip,
      },
    ],
  },
  {
    to: "group:time", label: "Time & Leave", icon: Clock, tone: "cyan",
    children: [
      { to: "/attendance", label: "Attendance", moduleKey: "attendance" },
      { to: "/leave",      label: "Leave",      moduleKey: "leave" },
      { to: "/timesheet",  label: "Timesheets", moduleKey: "timesheet" },
    ],
  },
  {
    to: "group:finance", label: "Finance", icon: Wallet, tone: "warning",
    children: [
      { to: "/payroll",  label: "Payroll",  moduleKey: "payroll" },
      { to: "/expenses", label: "Expenses", moduleKey: "expenses" },
    ],
  },
  {
    to: "group:performance", label: "Performance", icon: TrendingUp, tone: "accent",
    children: [
      { to: "/performance/goals", label: "Goals",   moduleKey: "performance" },
      { to: "/performance",       label: "Reviews", moduleKey: "performance", end: true },
    ],
  },
  {
    to: "group:development", label: "Talent Development", icon: GraduationCap, tone: "success",
    children: [
      { to: "/learning",   label: "Learning",   moduleKey: "learning" },
      { to: "/succession", label: "Succession", moduleKey: "succession" },
    ],
  },
  { to: "/assets",    label: "Assets",    icon: Laptop, tone: "brand",  moduleKey: "assets" },
  { to: "/grievance", label: "Grievance", icon: Scale,  tone: "danger", moduleKey: "grievance" },
  {
    to: "group:reports", label: "Reports", icon: BarChart3, tone: "info",
    children: [
      { to: "/reports/dashboard", label: "HR Dashboard",      moduleKey: "reports", when: isHr },
      { to: "/reports/employees", label: "Employee Reports",  moduleKey: "reports", when: isHr },
      { to: "/reports/headcount", label: "Headcount Reports", moduleKey: "reports", when: isHr },
      { to: "/reports/attrition", label: "Attrition Reports", moduleKey: "reports", when: isHr },
      { to: "/reports/probation", label: "Probation Reports", moduleKey: "reports", when: isHr },
      { to: "/reports/documents", label: "Document Reports",  moduleKey: "reports", when: isHr },
      { to: "/reports/leave",     label: "Leave Reports",     moduleKey: "reports", when: isHr },
      { to: "/reports/lop",       label: "LOP & Absenteeism", moduleKey: "reports", when: isHr },
      { to: "/reports/timesheet", label: "Timesheet Reports", moduleKey: "reports", when: isHr },
    ],
  },
  {
    to: "group:admin", label: "Admin", icon: Settings, tone: "rose",
    children: [
      { to: "/admin",                 label: "Admin",           moduleKey: "admin" },
      { to: "/admin/leave",           label: "Leave Admin",     moduleKey: "admin-leave" },
      { to: "/admin/email-templates", label: "Email Templates", moduleKey: "admin-email-templates" },
    ],
  },
];

// Fixed row geometry. Every offset the hover pill and the active marker use is
// derived from these, so neither needs a measured layout or a reflow.
/** Matches the 640px breakpoint of the phone sidebar rules in tokens.css. */
const PHONE_QUERY = "(max-width: 639px)";
/** Row geometry in px. The phone set is the desktop one at 75%; it must match
 *  the heights and margins in the "Sidebar on phones" block of tokens.css. */
const GEOMETRY = {
  desktop: { rowSlot: 38, rowH: 36, subSlot: 32, subH: 30, iconSize: ICON.size },
  phone:   { rowSlot: 29, rowH: 27, subSlot: 24, subH: 22, iconSize: 12 },
} as const;
const ROW_H = GEOMETRY.desktop.rowH;

function subscribePhone(onChange: () => void) {
  const mq = window.matchMedia(PHONE_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

interface Geom { y: number; h: number }

const childKey = (parent: string, child: string) => `${parent}>${child}`;

function pathMatches(to: string, end: boolean, pathname: string): boolean {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function initialsFrom(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function SidebarNav({ email, role }: { email: string; role: string }) {
  const { accessToken, setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const festive = useActiveFestival();
  const isPhone = useSyncExternalStore(subscribePhone, () => window.matchMedia(PHONE_QUERY).matches, () => false);
  const geo = isPhone ? GEOMETRY.phone : GEOMETRY.desktop;

  // On phones the expanded menu floats over the page, so with no saved choice it starts as the rail.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("hrms_sidebar_collapsed");
      if (saved !== null) return saved === "1";
    } catch { /* private mode */ }
    return window.matchMedia(PHONE_QUERY).matches;
  });
  const [sectionOverrides, setSectionOverrides] = useState<Record<string, boolean>>({});
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [cliCopied, setCliCopied] = useState(false);
  // HRMS-0653: the profile card zooms out of the avatar tile, so it needs the tile's centre.
  const [profileOrigin, setProfileOrigin] = useState<{ x: number; y: number } | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const issuesRef = useRef<HTMLDivElement>(null);
  const { issues: notifs, markRead, markAllRead, dismiss, clearRead } = useNotifications();
  const unreadCount = notifs.filter(n => !n.readAt).length;

  const visibleNav = useMemo(() => {
    const viewer = { role, isOnActivePip: me.isOnActivePip };
    return NAV_ITEMS.filter(item => item.children || (item.moduleKey !== undefined && me.moduleAccess.has(item.moduleKey)))
      .map(item =>
        item.children
          ? { ...item, children: item.children.filter(c => me.moduleAccess.has(c.moduleKey) && (!c.when || c.when(viewer))) }
          : item,
      )
      // A group whose every child is hidden for this viewer (Reports, for non-HR) is dropped.
      .filter(item => !item.children || item.children.length > 0);
  }, [me.moduleAccess, me.isOnActivePip, role]);

  /** A group starts open while the current page is one of its children. */
  const routeOpensSection = (to: string) =>
    visibleNav.find(item => item.to === to)?.children?.some(c => pathMatches(c.to, false, location.pathname)) ?? false;
  const isSectionOpen = (to: string) => sectionOverrides[to] ?? routeOpensSection(to);

  function setCollapsedState(next: boolean) {
    setCollapsed(next);
    try { localStorage.setItem("hrms_sidebar_collapsed", next ? "1" : "0"); } catch { /* private mode */ }
  }

  // On phones the open menu covers the page, so picking a destination folds it
  // back to the rail. Not persisted: it is not a choice about the desktop layout.
  useEffect(() => {
    if (window.matchMedia(PHONE_QUERY).matches) setCollapsed(true);
  }, [location.pathname]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Ctrl/Cmd+B is bold inside a field, so only claim it outside one.
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
      if (!typing && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed(c => {
          const next = !c;
          try { localStorage.setItem("hrms_sidebar_collapsed", next ? "1" : "0"); } catch { /* private mode */ }
          return next;
        });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (issuesRef.current && !issuesRef.current.contains(e.target as Node)) setIssuesOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  /** Where every visible row sits, in px from the top of the list. */
  const layout = useMemo(() => {
    const map = new Map<string, Geom>();
    let y = 0;
    for (const item of visibleNav) {
      map.set(item.to, { y, h: geo.rowH });
      y += geo.rowSlot;
      if (item.children && !collapsed && isSectionOpen(item.to)) {
        item.children.forEach((child, i) => {
          map.set(childKey(item.to, child.to), { y: y + i * geo.subSlot, h: geo.subH });
        });
        y += item.children.length * geo.subSlot;
      }
    }
    return map;
    // isSectionOpen reads both pieces of state the layout depends on.
  }, [visibleNav, collapsed, sectionOverrides, location.pathname, geo]);

  /** The single row the marker belongs to: the longest path that matches. */
  const active = useMemo(() => {
    let best: { key: string; parent: string | undefined; len: number } | null = null;
    const consider = (key: string, to: string, end: boolean, parent?: string) => {
      if (!pathMatches(to, end, location.pathname)) return;
      if (!best || to.length > best.len) best = { key, parent, len: to.length };
    };
    for (const item of visibleNav) {
      if (item.children) {
        for (const child of item.children) {
          consider(childKey(item.to, child.to), child.to, child.end ?? false, item.to);
        }
      } else {
        consider(item.to, item.to, false);
      }
    }
    return best as { key: string; parent: string | undefined; len: number } | null;
  }, [visibleNav, location.pathname]);

  // When the active page is a sub-item but its parent is shut (or the sidebar is
  // a rail), the marker falls back to the parent row and dims.
  const onHiddenChild = !!active?.parent && (collapsed || !isSectionOpen(active.parent));
  const markKey = active ? (onHiddenChild ? active.parent! : active.key) : null;
  const markGeom = markKey ? layout.get(markKey) ?? null : null;
  const markMode = !markGeom ? "none" : onHiddenChild ? "parent" : "on";

  const hoverGeom = hoverKey ? layout.get(hoverKey) ?? null : null;
  const pillOn = !!hoverGeom && hoverKey !== markKey;

  // The pill and the marker keep their last position while they fade out, so
  // they never slide back to the top of the list on the way.
  const lastPill = useRef<Geom>({ y: 0, h: ROW_H });
  const lastMark = useRef<Geom>({ y: 0, h: ROW_H });
  if (hoverGeom) lastPill.current = hoverGeom;
  if (markGeom) lastMark.current = markGeom;
  const pillGeom = hoverGeom ?? lastPill.current;
  const markerGeom = markGeom ?? lastMark.current;

  function toggleSection(to: string) {
    setSectionOverrides(prev => ({ ...prev, [to]: !(prev[to] ?? routeOpensSection(to)) }));
  }

  function onParentClick(to: string) {
    if (collapsed) {
      // In the rail there is nowhere to show children, so open the sidebar first.
      setCollapsedState(false);
      setSectionOverrides(prev => ({ ...prev, [to]: true }));
      return;
    }
    toggleSection(to);
  }

  function handleCopyCLIToken() {
    if (!accessToken) return;
    navigator.clipboard.writeText(`node tools/issues/index.mjs set-token ${accessToken}`).then(() => {
      setCliCopied(true);
      setTimeout(() => setCliCopied(false), 2000);
    });
  }

  async function handleLogout() {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    navigate("/login", { replace: true });
  }

  function clickNotif(n: NotifItem) {
    if (!n.readAt) markRead("issues", n.id);
    setIssuesOpen(false);
    if (n.link) navigate(n.link);
    else if (n.issueId) navigate(`/issues?highlight=${n.issueId}`);
  }

  /** In the rail the label is gone, so the name moves into a tooltip that also
   *  opens on keyboard focus. */
  const withTip = (label: string, node: React.ReactElement) =>
    collapsed
      ? <Tooltip content={label} side="right" align="center" sideOffset={10} contentClassName="text-[12.5px] font-semibold">{node}</Tooltip>
      : node;

  return (
    <aside className="sidenav" data-collapsed={collapsed}>
      {/* ── Brand ── */}
      <div className="sidenav-brand">
        {/* The wrapper is the tile's size, so the festive icon can sit over its corner without moving it. */}
        <span className="relative flex shrink-0">
          {withTip("My profile", (
            <button
              type="button"
              className="sidenav-logo sidenav-logo--photo"
              aria-label="Open my profile"
              aria-haspopup="dialog"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setProfileOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
                setProfileOpen(true);
              }}
            >
              <Avatar src={me.photoUrl} name={me.fullName || email} size="md" shape="rounded" />
            </button>
          ))}
          {festive && location.pathname === "/dashboard" && (
            <FestiveBadge active={festive} className="left-[68%] top-[-29%] h-[68%] w-[68%]" />
          )}
        </span>
        <span className="sidenav-label">
          <span className="sidenav-brand-name">HRMS</span>
          <span className="sidenav-brand-role">{role.replace(/_/g, " ")}</span>
        </span>
      </div>
      <ProfileCardDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        origin={profileOrigin}
        fallbackName={email}
      />

      <button
        type="button"
        className="sidenav-handle"
        onClick={() => setCollapsedState(!collapsed)}
        aria-expanded={!collapsed}
        aria-controls="sidenav-main"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={`${collapsed ? "Expand" : "Collapse"} sidebar (Ctrl+B)`}
      >
        <ChevronLeft size={15} strokeWidth={2} />
      </button>

      {/* ── Nav ── */}
      <nav id="sidenav-main" aria-label="Main" className="sidenav-list" onMouseLeave={() => setHoverKey(null)}>
        <div
          className="sidenav-rows"
          data-pill={pillOn ? "on" : "off"}
          data-mark={markMode}
          // Row offsets are computed from the fixed row heights above at runtime;
          // an arbitrary per-row px offset cannot be a static utility class.
          style={{
            "--sn-pill-y": `${pillGeom.y}px`,
            "--sn-pill-h": `${pillGeom.h}px`,
            "--sn-mark-y": `${markerGeom.y}px`,
            "--sn-mark-h": `${markerGeom.h}px`,
          } as CSSProperties}
        >
          <span className="sidenav-pill" aria-hidden="true" />
          <span className="sidenav-marker" aria-hidden="true" />

          {visibleNav.map(({ to, label, icon: Icon, tone, children }) => {
            const open = !collapsed && isSectionOpen(to);
            const subId = `sidenav-sub-${to.replace(/\W+/g, "-")}`;
            const rowProps = {
              className: "sidenav-row",
              onMouseEnter: () => setHoverKey(to),
              onFocus: () => setHoverKey(to),
              onBlur: () => setHoverKey(null),
            };

            return (
              <div key={to}>
                {children ? (
                  withTip(label, (
                    <button
                      type="button"
                      {...rowProps}
                      onClick={() => onParentClick(to)}
                      aria-expanded={open}
                      aria-controls={subId}
                      data-open={open}
                      data-active={markKey === to && markMode === "on"}
                      // Present whenever the current page is one of this row's
                      // children; "marked" additionally means the marker has
                      // fallen back onto this row because they are hidden.
                      data-subactive={
                        active?.parent === to ? (markMode === "parent" ? "marked" : "open") : undefined
                      }
                    >
                      <span className="sidenav-icon" data-tone={tone}><Icon size={geo.iconSize} strokeWidth={ICON.strokeWidth} /></span>
                      <span className="sidenav-label">{label}</span>
                      {!collapsed && <ChevronRight className="sidenav-chev" size={12} strokeWidth={2} />}
                    </button>
                  ))
                ) : (
                  withTip(label, (
                    <Link
                      to={to}
                      {...rowProps}
                      data-active={markKey === to}
                      aria-current={markKey === to ? "page" : undefined}
                    >
                      <span className="sidenav-icon" data-tone={tone}><Icon size={geo.iconSize} strokeWidth={ICON.strokeWidth} /></span>
                      <span className="sidenav-label">{label}</span>
                    </Link>
                  ))
                )}

                {children && !collapsed && (
                  <div
                    id={subId}
                    className="sidenav-sub"
                    data-open={open}
                    data-tone={tone}
                    // Drives the open height; the child count is only known at runtime.
                    style={{ "--sn-sub-count": children.length } as CSSProperties}
                  >
                    {children.map(child => {
                      const key = childKey(to, child.to);
                      return (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="sidenav-subrow"
                          data-active={markKey === key}
                          aria-current={markKey === key ? "page" : undefined}
                          tabIndex={open ? undefined : -1}
                          onMouseEnter={() => setHoverKey(key)}
                          onFocus={() => setHoverKey(key)}
                          onBlur={() => setHoverKey(null)}
                        >
                          <span className="sidenav-dot" aria-hidden="true" />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* ── Footer ── */}
      <div className="sidenav-footer">
        <div ref={issuesRef} className="relative">
          {withTip("Issues", (
            <button
              type="button"
              className="sidenav-chip"
              onClick={() => setIssuesOpen(o => !o)}
              aria-expanded={issuesOpen}
              aria-label={unreadCount > 0 ? `Issues, ${unreadCount} unread` : "Issues"}
            >
              <Bug size={16} strokeWidth={1.8} />
              <span className="sidenav-label">Issues</span>
              {unreadCount > 0 && (
                <span className="sidenav-badge" data-dot={collapsed} aria-hidden="true">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          ))}

          {issuesOpen && (
            <div className="absolute bottom-[calc(100%+6px)] left-0 z-[200] max-h-[340px] w-[300px] overflow-y-auto rounded-md border border-[var(--color-border)] bg-surface-0 shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
              <div className="flex items-center justify-between border-b border-[var(--color-border)] px-3 py-2.5">
                <span className="text-xs font-semibold text-surface-900">Issues</span>
                <div className="flex gap-2.5">
                  {notifs.some(n => n.readAt) && (
                    <button onClick={() => clearRead("issues")} className="text-[11px] text-surface-500 hover:text-surface-700">
                      Clear read
                    </button>
                  )}
                  {unreadCount > 0 && (
                    <button onClick={() => markAllRead("issues")} className="text-[11px] text-brand-600 hover:text-brand-700">
                      Mark all read
                    </button>
                  )}
                </div>
              </div>
              {notifs.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-surface-400">No notifications yet</div>
              ) : notifs.map(n => (
                <div
                  key={n.id}
                  onClick={() => clickNotif(n)}
                  className={`flex cursor-pointer items-start gap-2 border-b border-[var(--color-border)] px-3 py-2.5 ${n.readAt ? "" : "bg-brand-50"}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs leading-snug text-surface-800">{n.message}</div>
                    <div className="mt-0.5 text-[11px] text-surface-400">
                      {n.displayIdFormatted ? `${n.displayIdFormatted} · ` : ""}
                      {new Date(n.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss("issues", n.id); }}
                    title="Dismiss"
                    className="shrink-0 px-0.5 text-surface-400 hover:text-surface-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Keyed so the swapped layout cross-fades instead of snapping. */}
        <div className="sidenav-footer-body" key={collapsed ? "rail" : "wide"}>
          {collapsed ? (
            <>
              <span className="sidenav-avatar mx-auto" title={email} aria-hidden="true">{initialsFrom(email)}</span>
              <Tooltip content="Sign out" side="right" align="center" sideOffset={10} contentClassName="text-[12.5px] font-semibold">
                <button type="button" className="sidenav-iconbtn mx-auto" onClick={() => void handleLogout()} aria-label="Sign out">
                  <LogOut size={15} strokeWidth={1.8} />
                </button>
              </Tooltip>
            </>
          ) : (
            <>
              <span className="sidenav-email" title={email}>{email}</span>
              <ThemeToggle />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="sidenav-iconbtn"
                  data-ok={cliCopied}
                  onClick={handleCopyCLIToken}
                  aria-label={cliCopied ? "CLI token copied" : "Copy CLI token"}
                  title={cliCopied ? "Copied!" : "Copy CLI token"}
                >
                  {cliCopied ? <CheckCircle2 size={15} strokeWidth={1.8} /> : <Copy size={15} strokeWidth={1.8} />}
                </button>
                <button type="button" className="sidenav-iconbtn sidenav-signout" onClick={() => void handleLogout()}>
                  <LogOut size={15} strokeWidth={1.8} />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
